import { HttpErrorResponse } from '@angular/common/http';
import {
  DestroyRef,
  ErrorHandler,
  inject,
  Injectable,
  NgZone,
} from '@angular/core';
import * as Sentry from '@sentry/angular';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { environment } from '../environments/environment';
import { localStorageKeys } from './shared/constants';
import { ErrorReportingBridgeService } from '@boldpenguin/emperor-services';

declare global {
  interface Window {
    Cypress?: unknown;
  }
}

@Injectable({ providedIn: 'root' })
export class AppErrorHandler implements ErrorHandler {
  private errorReportingBridgeService = inject(ErrorReportingBridgeService);
  private _destroyRef = inject(DestroyRef);
  private _zone = inject(NgZone);

  hideReportDialog: boolean;

  private chunkFailedPattern = /Loading chunk [\d]+ failed/;

  handleError(e: any) {
    const extractedError = this.extractError(e) || 'Handled unknown error';

    // note: This is the `production` field on the config (roughly meaning
    // any deployed environment, not the prod environment)
    if (environment.production) {
      if (this.chunkFailedPattern.test(e.message)) {
        window.location.reload();
        return;
      }

      console.error(extractedError);
      const isAutomatedTest = !!window?.Cypress;
      const sentryModalDisabled = localStorage.getItem(
        localStorageKeys.disableSentryModal,
      );

      const eventId = this._zone.runOutsideAngular(() =>
        Sentry.captureException(extractedError),
      );

      if (
        environment.isUatEnv &&
        !this.hideReportDialog &&
        !sentryModalDisabled &&
        !isAutomatedTest
      ) {
        this.hideReportDialog = true;
        Sentry.showReportDialog({ eventId });
      }
    } else {
      console.error('Sentry', extractedError);
    }
  }

  handleErrorsFromExceptionsBridge() {
    this.errorReportingBridgeService.error$
      .pipe(filter(Boolean), takeUntilDestroyed(this._destroyRef))
      .subscribe((error) => {
        this.handleError(error);
      });
  }

  // https://github.com/getsentry/sentry-javascript/blob/master/packages/angular/src/errorhandler.ts
  private extractError(error: any): unknown {
    // Try to unwrap zone.js error.
    // https://github.com/angular/angular/blob/master/packages/core/src/util/errors.ts
    if (error?.ngOriginalError) {
      error = error.ngOriginalError;
    }

    // We can handle messages and Error objects directly.
    if (typeof error === 'string' || error instanceof Error) {
      return error;
    }

    // If it's http module error, extract as much information from it as we can.
    if (error instanceof HttpErrorResponse) {
      // The `error` property of http exception can be either an `Error` object, which we can use directly...
      if (error.error instanceof Error) {
        return error.error;
      }

      // ... or an`ErrorEvent`, which can provide us with the message but no stack...
      if (error.error instanceof ErrorEvent && error.error.message) {
        return error.error.message;
      }

      // ...or the request body itself, which we can use as a message instead.
      if (typeof error.error === 'string') {
        return `Server returned code ${error.status} with body "${error.error}"`;
      }

      // If we don't have any detailed information, fallback to the request message itself.
      return error.message;
    }

    // Nothing was extracted, fallback to default error message.
    return null;
  }
}
